import * as React from 'react';
import PropTypes from 'prop-types';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import Paper from '@material-ui/core/Paper';
import styled from 'styled-components';
import { breakpoints } from '@styles/index';
import IconButton from '@base/IconButton/IconButton';
import ChevronLeft from '@icon/ChevronLeft';
import ChevronRight from '@icon/ChevronRight';
import Container, { Title } from './styles';

const BackgroundColor = styled.div`
  background: #fcfcfc;
  padding-top: 120px;
  padding-bottom: 88px;
  @media (max-width: ${breakpoints.tablet}) {
    padding-top: 80px;
    padding-bottom: 48px;
  }
  @media (max-width: ${breakpoints.mobile}) {
    background: transparent;
    padding: 40px 16px 2px;
  }
`;

const StyledTitle = styled(Title)`
  grid-column: span 24;
  text-align: center;
  @media (max-width: ${breakpoints.tablet}) {
    text-align: left;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  grid-column: span 24;
  overflow: hidden;
  width: 1136px;
  margin: auto;
  @media (max-width: ${breakpoints.tablet}) {
    width: 728px;
  }
  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
  }
`;

const Carousel = styled.div`
  grid-column: span 24;
  margin-top: 76px;
  padding-left: 32px;
  padding-bottom: 32px;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(4, 1fr);
  transform: ${({ $currentIndex }) =>
    ($currentIndex && `translate3d(${-296 * $currentIndex}px, 0, 0)`) ||
    'initial'};
  transition: transform 400ms ease-in;
  @media (max-width: ${breakpoints.tablet}) {
    display: flex;
    padding-top: 48px;
    margin-top: 0;
    overflow: auto;
    overscroll-behavior: unset;
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    padding-left: 0;
  }
  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 0;
    padding-top: 40px;
    box-shadow: 0px 16px 32px #f0f4f7;
  }
`;

const Card = styled(Paper)`
  background: #ffffff;
  box-shadow: 0px 16px 32px #f0f4f7;
  border-radius: 8px;
  padding: 40px 24px 24px 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 264px;
  margin-right: 16px;
  @media (max-width: ${breakpoints.tablet}) {
    width: 256px;
    height: auto;
    height: 328px;
  }
`;

const CardTitle = styled(H5)`
  font-weight: 400;
  padding-bottom: 40px;
`;

const Divider = styled.div`
  background: #ffffff;
  box-shadow: inset 0px 1px 0px #f0f4f7;
  height: 16px;
`;

const Content = styled.div`
  display: flex;
`;

const CardImage = styled.img`
  border-radius: 20px;
  object-fit: cover;
  margin-left: 16px;
  cursor: pointer;
  user-drag: none;
  user-select: none;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ParentName = styled(P1)`
  font-weight: bold;
  line-height: 150%;
`;
const ChildName = styled(P1)`
  font-size: 12px;
  line-height: 150%;
`;

const Quote = styled.img`
  width: 56px;
  height: 56px;
  position: absolute;
  top: -28px;
  cursor: pointer;
  user-drag: none;
  user-select: none;
  object-fit: cover;
`;

const Column = styled.div`
  flex-direction: column;
  display: flex;
  justify-content: space-between;
  flex: 1;
`;

const Icon = styled(IconButton)`
  outline: none;
  padding: 0;
  border: none;
  position: absolute;
  top: 50%;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;

const LeftIcon = styled(Icon)`
  left: 16px;
`;

const RightIcon = styled(Icon)`
  right: 0;
`;

const Buttons = styled.div`
  @media (max-width: ${breakpoints.tablet}) {
    display: none;
  }
`;
function Comments({ data }) {
  const { title, comments } = data;
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const next = () =>
    currentIndex < comments.length - 4
      ? setCurrentIndex((index) => index + 1)
      : null;
  const prev = () =>
    currentIndex > 0
      ? setCurrentIndex((index) => index - 1)
      : setCurrentIndex(0);
  const renderButtons = () => {
    return (
      <Buttons>
        <LeftIcon onClick={prev} $visible={currentIndex !== 0}>
          <ChevronLeft size="24px" aria-hidden="true" color="3C4852" />
        </LeftIcon>
        <RightIcon onClick={next} $visible={currentIndex < comments.length - 4}>
          <ChevronRight size="24px" aria-hidden="true" color="3C4852" />
        </RightIcon>
      </Buttons>
    );
  };
  return (
    <BackgroundColor>
      <Container>
        <StyledTitle>{title}</StyledTitle>
        <CarouselContainer>
          <Carousel $currentIndex={currentIndex}>
            {comments.map((_comment) => (
              <Card key={_comment.id}>
                <Column>
                  <Quote
                    src={_comment.badgeImage}
                    width="auto"
                    height="auto"
                    loading="lazy"
                  />
                  <CardTitle>{_comment.description}</CardTitle>
                  <Divider />
                </Column>
                <Content>
                  {_comment.image && (
                    <CardImage
                      src={_comment.image}
                      width="40"
                      height="40"
                      loading="lazy"
                    />
                  )}
                  <CardInfo>
                    <ParentName>{_comment.title}</ParentName>
                    <ChildName>{_comment.actionText}</ChildName>
                  </CardInfo>
                </Content>
              </Card>
            ))}
          </Carousel>
          {renderButtons()}
        </CarouselContainer>
      </Container>
    </BackgroundColor>
  );
}

Comments.propTypes = {
  title: PropTypes.string,
  data: PropTypes.objectOf(PropTypes.any).isRequired
};

Comments.defaultProps = {
  title: ''
};

export default Comments;
